import React, { useEffect, useRef, useState } from 'react';
import AiSparklesIcon from 'components/general/ai_sparkles_icon/ai_sparkles_icon';
import InfoIcon from 'components/general/info_icon/info_icon';
import ExtractionModal from './extraction_modal';
import FileUploadSection from './file_upload_section';
import TabSelector from './tab_selector';
import Tooltip from 'components/general/tooltip/tooltip';
import { useChannel } from 'lib/use_channel';

const styles = require('./ai_assist.module.scss');

interface AiAssistProps {
  extractPath: string;
  extractFilePath: string;
}

const MIN_TEXT_LENGTH = 1;
// Extraction runs in a Sidekiq worker (retry: false), so if the ActionCable
// completion never arrives (dropped socket, crashed worker) the modal would
// hang forever. The timeout is a "still working, try again" escape rather than
// a failure, and it has to cover the Sidekiq queue wait, so keep it generous.
const EXTRACTION_TIMEOUT_MS = 90000;
const GENERIC_ERROR = 'An unexpected error occurred. Please try again.';
const TIMEOUT_NOTICE = 'This is taking longer than expected. Please try again.';

export default function AiAssist({ extractPath, extractFilePath }: AiAssistProps) {
  const [activeTab, setActiveTab] = useState('text');
  const [textContent, setTextContent] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isFileLoading, setIsFileLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const watchdogRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // True while an attempt is in flight. Once the watchdog fires we set it false
  // so a late broadcast can't yank the user into the wizard after we told them
  // to try again.
  const attemptLiveRef = useRef(false);

  const clearWatchdog = () => {
    if (watchdogRef.current) {
      clearTimeout(watchdogRef.current);
      watchdogRef.current = null;
    }
  };

  const endAttempt = () => {
    attemptLiveRef.current = false;
    clearWatchdog();
    setIsLoading(false);
  };

  const showError = (message: string) => {
    endAttempt();
    if (window.Alerts) {
      window.Alerts.alert(message);
    }
  };

  // Release the spinner on unmount so a pending timer can't call setState on an
  // unmounted component.
  useEffect(() => clearWatchdog, []);

  // Subscribe on mount so no completion broadcast is missed (ActionCable does
  // not buffer messages sent before the subscription lands).
  useChannel<{ status: string; redirect_url?: string; error?: string }>(
    'JobAdExtractionChannel',
    (data) => {
      if (!attemptLiveRef.current) {
        return;
      }

      if (data.status === 'complete' && data.redirect_url) {
        attemptLiveRef.current = false;
        clearWatchdog();
        window.location.href = data.redirect_url;
      } else {
        showError(data.error || GENERIC_ERROR);
      }
    }
  );

  const isTextButtonEnabled = textContent.trim().length >= MIN_TEXT_LENGTH;
  const isFileButtonEnabled = selectedFile !== null;
  const isButtonEnabled = activeTab === 'text' ? isTextButtonEnabled : isFileButtonEnabled;

  const handleFileSelect = (file: File) => {
    setIsFileLoading(true);
    setTimeout(() => {
      setSelectedFile(file);
      setIsFileLoading(false);
    }, 500);
  };

  const handleFileRemove = () => {
    setSelectedFile(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isButtonEnabled || isLoading) {
      return;
    }

    setIsLoading(true);
    attemptLiveRef.current = true;

    const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || '';

    try {
      let response: Response;

      if (activeTab === 'text') {
        response = await fetch(extractPath, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': csrfToken,
          },
          body: JSON.stringify({ job_text: textContent }),
        });
      } else {
        const formData = new FormData();
        formData.append('file', selectedFile as File);

        response = await fetch(extractFilePath, {
          method: 'POST',
          headers: {
            'X-CSRF-Token': csrfToken,
          },
          body: formData,
        });
      }

      if (response.status === 202) {
        // Extraction accepted; keep the modal open until JobAdExtractionChannel
        // broadcasts the outcome. If nothing arrives in time, treat it as still
        // running (not a failure) and let the user try again.
        clearWatchdog();
        watchdogRef.current = setTimeout(() => {
          endAttempt();
          if (window.Alerts) {
            window.Alerts.notice(TIMEOUT_NOTICE);
          }
        }, EXTRACTION_TIMEOUT_MS);
        return;
      }

      const data = await response.json();
      showError(data.error || GENERIC_ERROR);
    } catch {
      showError(GENERIC_ERROR);
    }
  };

  const placeholderText = `Write or paste your job information here. It can be in any language.

You can include:
• Job title
• Required skills and qualifications
• Responsibilities
• Benefits and perks
• Location and work model

Don't worry about formatting. AI will organize everything for you.`;

  const renderTextInputSection = () => (
    <div className={styles.inputSection}>
      <div className={styles.inputHeader}>
        <div className={styles.labelRow}>
          <label htmlFor="jobDescription" className={styles.label}>
            Share your job description
          </label>
          <Tooltip
            text="AI extracts and prefills job details such as title, description, and skills. You can review and fill in what's missing after extraction."
          >
            <button type="button" className={styles.infoIcon} aria-label="Information">
              <InfoIcon width="18" height="18" />
            </button>
          </Tooltip>
        </div>
        <p className={styles.inputDescription}>
          Include all your job details (any language). Our AI will generate the final job post in English.
        </p>
      </div>

      <textarea
        id="jobDescription"
        className={styles.textarea}
        placeholder={placeholderText}
        value={textContent}
        onChange={(e) => setTextContent(e.target.value)}
        disabled={isLoading}
      />
    </div>
  );

  const getTooltipText = () => {
    if (activeTab === 'text') {
      return 'Please share a job description';
    }
    return 'Please upload a file';
  };

  return (
    <>
      <ExtractionModal isOpen={isLoading} />
      <div className={styles.pageWrapper}>
        <div className={styles.contentCard}>
          <div className={styles.aiAssistContainer}>
            <div className={styles.header}>
              <div className={styles.titleRow}>
                <AiSparklesIcon className={styles.icon} width="20" height="20" />
                <h1 className={styles.title}>Post a Job - AI Assisted</h1>
              </div>
              <p className={styles.subtitle}>Choose how you want to share your job information.</p>
            </div>

            <form onSubmit={handleSubmit} className={styles.form}>
              <div className={styles.content}>
                <TabSelector activeTab={activeTab} onTabChange={setActiveTab} />

                {activeTab === 'text' && renderTextInputSection()}

                {activeTab === 'file' && (
                  <FileUploadSection
                    selectedFile={selectedFile}
                    isFileLoading={isFileLoading}
                    onFileSelect={handleFileSelect}
                    onFileRemove={handleFileRemove}
                    isDisabled={isLoading}
                  />
                )}

                {!isButtonEnabled && !isLoading ? (
                  <Tooltip
                    text={getTooltipText()}
                    className={styles.tooltipFullWidth}
                    style={{
                      left: '50%',
                      marginLeft: '-5.625rem',
                    }}
                  >
                    <button
                      type="submit"
                      className={styles.extractButton}
                      disabled={true}
                    >
                      <AiSparklesIcon className={styles.buttonIcon} width="20" height="20" fill="currentColor" />
                      Extract job information
                    </button>
                  </Tooltip>
                ) : (
                  <button
                    type="submit"
                    className={`${styles.extractButton} ${isButtonEnabled && !isLoading ? styles.extractButtonEnabled : ''
                      }`}
                    disabled={!isButtonEnabled || isLoading}
                  >
                    <AiSparklesIcon className={styles.buttonIcon} width="20" height="20" fill="currentColor" />
                    {isLoading ? 'Extracting...' : 'Extract job information'}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}
